import React from 'react';
import { FormikProps } from 'formik';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CommonTheme, useCurrentTheme } from 'app/shared/theme';
import { errorMsgForField } from 'app/shared/utils/form';
import { useMultiImageUpload } from 'app/shared/utils/useMultiImageUpload';
import { AspectRatio, MultiImageFormData } from 'app/shared/typings';
import {
  withMultiImageDropzone,
  WithMultiImageDropzoneProps,
} from 'app/shared/components/styleUtils/withMultiImageDropzone';
import {
  Box,
  InputHelperText,
  InputLabel,
  MultiImageDropzone,
} from 'app/shared/components/atoms';

interface Props {
  objectType: string;
  objectId?: number;
  imageFieldName: string;
  label: string;
  captionText: string;
  ratio: AspectRatio;
  multipleImages?: boolean;
  setIsSubmitButtonDisabled: (disabled: boolean) => void;
  maxFileSizeMegabytes?: number;
  formikProps: FormikProps<MultiImageFormData<any>>;
  hide?: boolean;
  minHeight?: string;
}

const Container = styled(Box)<WithMultiImageDropzoneProps>`
  ${withMultiImageDropzone};
`;

export const SingleRatioImageMultiUploader = ({
  objectType,
  objectId,
  imageFieldName,
  label,
  captionText,
  ratio,
  multipleImages,
  setIsSubmitButtonDisabled,
  maxFileSizeMegabytes = 2,
  formikProps,
  hide,
  minHeight = '160px',
}: Props) => {
  const { formatMessage } = useIntl();
  const theme = useCurrentTheme() as CommonTheme;
  const { touched, errors, setSubmitting } = formikProps;

  const {
    imagesForAllRatios,
    setImagesForAllRatios,
    failedFiles,
    setFailedFiles,
  } = useMultiImageUpload({
    formikProps,
    imageFieldName,
    setIsSubmitButtonDisabled,
    setIsSubmitting: setSubmitting,
  });

  const getFailedFileError = () => {
    if (failedFiles.length > 0) {
      return formatMessage(
        { id: 'shared.multiImageEdit.pleaseRemoveFailedFiles' },
        { count: failedFiles.length }
      );
    }
    return null;
  };

  const error =
    errorMsgForField(imageFieldName, touched, errors) || getFailedFileError();

  return (
    <Container
      images={imagesForAllRatios}
      thumbnailAspectRatio={ratio.replace(':', '/')}
      visibility={hide ? 'hidden' : 'visible'}
      h={hide ? '0px' : 'fit-content'}
      minHeight={hide ? '0px' : minHeight}
    >
      <Box flexDirection="column" mb={2} visibility="inherit" h="inherit">
        <InputLabel
          label={label}
          name={imageFieldName}
          hasError={!!error}
          mb={0}
        />
        <InputHelperText
          hasError={!!error}
          isDisabled={false}
          isSuccessful={false}
          fontSize="fontSize75"
        >
          {captionText}
        </InputHelperText>
      </Box>
      <MultiImageDropzone
        imagesForRatio={imagesForAllRatios}
        imagesForAllRatios={imagesForAllRatios}
        setImagesForAllRatios={setImagesForAllRatios}
        setFailedFiles={setFailedFiles}
        colors={{
          error: theme.colors.messaging.error.dark,
          primary: theme.colors.button.primary.background,
          active: theme.colors.button.primary.hover,
          shade100: theme.colors.primary.black.base,
          shade200: theme.colors.button.secondary.text,
          shade300: theme.colors.button.secondary.hover,
          shade400: theme.colors.primary.black.base,
          shade600: theme.colors.accent.primaryAccent.base,
          shade700: theme.colors.primary.white.base,
          shade800: theme.colors.primary.white.lighter,
          shade900: theme.colors.primary.black.base,
        }}
        fontFamily={theme.fonts.regular}
        ratio={ratio}
        multipleImagesPerRatio={multipleImages}
        maxFileSizeMegabytes={maxFileSizeMegabytes}
        mimeTypes={['image/jpeg', 'image/png']}
        objectType={objectType}
        objectId={objectId}
        width="100%"
        height={minHeight}
      />
      {!!error && (
        <InputHelperText
          hasError={true}
          isDisabled={false}
          isSuccessful={false}
        >
          {error}
        </InputHelperText>
      )}
    </Container>
  );
};
